import { ApiClientError, NetworkError } from '@recipes/api-client';
import type {
  ShoppingItemUpdateInput,
  ShoppingItemWriteInput,
  ShoppingList,
  ShoppingListItem,
} from '@recipes/contracts';
import {
  classifyGroceryCategory,
  decideShoppingAdd,
  formatQuantityAmount,
  normalizeIngredientName,
  type ShoppingAddOutcome,
} from '@recipes/domain';
import { lastKnownPosition } from '@/features/catalog/position';
import { api } from '@/lib/api';
import { authStore } from '@/lib/auth/auth-store';
import { currentLocale } from '@/lib/i18n';
import { offlineDb, type PendingShoppingAction } from '@/lib/offline/db';
import { getPlatform } from '@/lib/platform';
import { notifyDropped, storeListDetail, toItemRow } from './device-copy';

/**
 * Local-first writes for shopping lists (docs/partial/shopping-list-extensions-spec.md 5.7, 6.7). Every
 * change is applied to the device copy and queued in one transaction, then replayed in order.
 * The no-duplicate rules run locally with the same domain function the server uses; the server
 * applies them again at replay and has the last word. Nothing is dropped on 401 or network
 * errors; a change whose target is gone is dropped with a notice, never silently.
 */

export interface ShoppingReplayResult {
  done: number;
  dropped: number;
  offline: boolean;
}

interface AddPayload {
  input: ShoppingItemWriteInput;
  /** The add created a row on this device that the server does not know under this id. */
  created: boolean;
}

type CheckPayload = Required<Pick<ShoppingItemUpdateInput, 'checked' | 'checkedAt'>> &
  Pick<ShoppingItemUpdateInput, 'productId' | 'checkedLat' | 'checkedLng'>;

const now = () => new Date().toISOString();
const me = () => authStore.getState().user;
const QUEUE_TABLES = () =>
  [
    offlineDb.shoppingLists,
    offlineDb.shoppingItems,
    offlineDb.pendingShopping,
    offlineDb.shoppingMeta,
  ] as const;

const aliasKey = (id: string) => `alias:${id}`;

/**
 * The row a user action is about. The screen may still show an item under the id it had a
 * moment ago (the server merged it into someone else's item meanwhile); then the alias leads
 * to the row it became. Null when the item is gone – the action has nothing to act on.
 */
async function currentItem(item: ShoppingListItem): Promise<ShoppingListItem | null> {
  const row = await offlineDb.shoppingItems.get(item.id);
  if (row) return row.item;
  const alias = (await offlineDb.shoppingMeta.get(aliasKey(item.id)))?.value;
  if (typeof alias !== 'string') return null;
  return (await offlineDb.shoppingItems.get(alias))?.item ?? null;
}

function isPermanent(error: unknown): boolean {
  return (
    error instanceof ApiClientError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 401 &&
    error.status !== 408 &&
    error.status !== 429
  );
}

async function enqueue(
  action: Omit<PendingShoppingAction, 'id' | 'createdAt' | 'attempts' | 'key'> & { key?: string },
): Promise<void> {
  await offlineDb.pendingShopping.add({
    key: crypto.randomUUID(),
    ...action,
    createdAt: Date.now(),
    attempts: 0,
  });
}

/** Counts of the list row follow the items on the device. */
async function recount(listId: string): Promise<void> {
  const row = await offlineDb.shoppingLists.get(listId);
  if (!row) return;
  const items = await offlineDb.shoppingItems.where('listId').equals(listId).toArray();
  await offlineDb.shoppingLists.put({
    ...row,
    list: {
      ...row.list,
      itemCount: items.length,
      checkedCount: items.filter((r) => r.item.checked).length,
    },
  });
}

const withDisplay = (item: ShoppingListItem): ShoppingListItem => ({
  ...item,
  display: formatQuantityAmount(item, currentLocale()),
});

// ---------------------------------------------------------------------------
// Replay
// ---------------------------------------------------------------------------

/** The server answered with another item than the one we changed locally: re-point the rest. */
async function repointItem(from: string, to: string, removeLocal: boolean): Promise<void> {
  if (from === to) return;
  await offlineDb.shoppingMeta.put({ key: aliasKey(from), value: to });
  await offlineDb.pendingShopping.where('entityId').equals(from).modify({ entityId: to });
  if (removeLocal) await offlineDb.shoppingItems.delete(from);
}

/** Stores what the server said – unless a later queued change for the item is still waiting. */
async function confirmItem(item: ShoppingListItem, actionId: number): Promise<void> {
  const waiting = await offlineDb.pendingShopping
    .where('entityId')
    .equals(item.id)
    .filter((a) => a.id !== actionId)
    .count();
  if (waiting === 0) await offlineDb.shoppingItems.put(toItemRow(item));
}

async function runAction(action: PendingShoppingAction): Promise<void> {
  switch (action.kind) {
    case 'list-create': {
      const body = action.payload as {
        name: string;
        type?: string | null;
        householdId?: string | null;
      };
      const list = await api.shopping.create({ ...body, id: action.listId });
      const row = await offlineDb.shoppingLists.get(action.listId);
      await offlineDb.shoppingLists.put({
        id: list.id,
        list: { ...list, itemCount: row?.list.itemCount ?? 0 },
      });
      return;
    }
    case 'item-add': {
      const payload = action.payload as AddPayload;
      const response = await api.shopping.addItems(
        action.listId,
        { items: [payload.input] },
        action.key,
      );
      const item = response.results[0]?.item;
      if (!item) return;
      await repointItem(action.entityId, item.id, payload.created);
      await confirmItem(item, action.id!);
      return;
    }
    case 'item-check':
    case 'item-update': {
      let item: ShoppingListItem;
      try {
        item = await api.shopping.updateItem(
          action.listId,
          action.entityId,
          action.payload as ShoppingItemUpdateInput,
        );
      } catch (error) {
        // An edit made offline against a row that moved on: what the user typed is applied on
        // top of the current state (they could not have seen the newer one).
        const current =
          error instanceof ApiClientError && error.status === 409
            ? (error.error.details as { current?: ShoppingListItem } | undefined)?.current
            : undefined;
        if (!current) throw error;
        item = await api.shopping.updateItem(action.listId, action.entityId, {
          ...(action.payload as ShoppingItemUpdateInput),
          baseUpdatedAt: current.updatedAt,
        });
      }
      await confirmItem(item, action.id!);
      return;
    }
    case 'item-remove': {
      try {
        await api.shopping.removeItem(action.listId, action.entityId);
      } catch (error) {
        // Already gone on the server is what we wanted.
        if (!(error instanceof ApiClientError && error.status === 404)) throw error;
      }
      return;
    }
    case 'clear-bought': {
      await storeListDetail(await api.shopping.bulk(action.listId, { action: 'REMOVE_CHECKED' }));
      return;
    }
  }
}

let running: Promise<ShoppingReplayResult> | null = null;

/** Replays the queue in order. Stops when the network or the session is not there. */
export function replayShoppingQueue(): Promise<ShoppingReplayResult> {
  if (running) return running;
  running = (async () => {
    const result: ShoppingReplayResult = { done: 0, dropped: 0, offline: false };
    const touched = new Set<string>();
    // Not a snapshot: whatever the user does while a request is under way (add, then check
    // off at once) is picked up by this very run instead of waiting for the next trigger.
    let lastId = 0;
    for (;;) {
      const action = await offlineDb.pendingShopping.where('id').above(lastId).first();
      if (!action) break;
      lastId = action.id!;
      try {
        await runAction(action);
        await offlineDb.pendingShopping.delete(action.id!);
        touched.add(action.listId);
        result.done++;
      } catch (error) {
        if (
          error instanceof NetworkError ||
          (error instanceof ApiClientError && error.status === 401)
        ) {
          result.offline = true;
          break;
        }
        if (!isPermanent(error)) {
          await offlineDb.pendingShopping.update(action.id!, {
            attempts: action.attempts + 1,
            lastError: error instanceof Error ? error.message : String(error),
          });
          break;
        }
        // The target is gone or the change is not allowed any more (item deleted by someone
        // else, list unshared, role changed): drop it and tell the user.
        await offlineDb.pendingShopping.delete(action.id!);
        await offlineDb.shoppingItems
          .where('id')
          .equals(action.entityId)
          .modify((row) => {
            delete row.pending;
            // What the device shows was refused: the server's row wins, whatever its age.
            row.item = { ...row.item, updatedAt: '1970-01-01T00:00:00.000Z' };
          });
        touched.add(action.listId);
        result.dropped++;
      }
    }
    // Rows without queued changes are confirmed; their lists show the server's counts again.
    for (const listId of touched) {
      const waiting = new Set(
        (await offlineDb.pendingShopping.where('listId').equals(listId).toArray()).map(
          (a) => a.entityId,
        ),
      );
      await offlineDb.shoppingItems
        .where('listId')
        .equals(listId)
        .filter((row) => Boolean(row.pending) && !waiting.has(row.id))
        .modify((row) => {
          delete row.pending;
        });
      if (result.dropped > 0 && !result.offline) {
        try {
          await storeListDetail(await api.shopping.get(listId));
        } catch {
          // gone or offline – the next delta sync reconciles
        }
      }
      await recount(listId);
    }
    notifyDropped(result.dropped);
    return result;
  })().finally(() => {
    running = null;
  });
  return running;
}

async function replayIfOnline(): Promise<void> {
  const platform = await getPlatform();
  if (!platform.isOnline()) return;
  const result = await replayShoppingQueue();
  if (result.offline) return;
  // We may have joined a run that was just finishing: what we queued could have missed it.
  const untried = await offlineDb.pendingShopping.filter((a) => a.attempts === 0).count();
  if (untried > 0) await replayShoppingQueue();
}

/** Sends what is queued without making the caller wait for the network. */
export function kickShoppingReplay(onDone?: () => void): void {
  void replayIfOnline()
    .catch(() => undefined)
    .then(() => onDone?.());
}

// ---------------------------------------------------------------------------
// Local-first operations
// ---------------------------------------------------------------------------

export interface LocalAddResult {
  outcome: ShoppingAddOutcome;
  item: ShoppingListItem;
  /** The item as it was before (re-activation, merge) – for Undo. */
  previous: ShoppingListItem | null;
}

export interface AddOptions {
  reactivateQuantity?: 'NEW' | 'KEEP';
  /** Wait for the replay before resolving (default); hands-free batches pass false. */
  replay?: boolean;
}

/**
 * Adds something to a list with the rules of 5.5 applied on the device: merge into an open
 * item, re-activate a bought one, or create a new item.
 */
export async function addItemLocal(
  listId: string,
  input: ShoppingItemWriteInput,
  options: AddOptions = {},
): Promise<LocalAddResult> {
  const result = await offlineDb.transaction(
    'rw',
    [...QUEUE_TABLES(), offlineDb.shoppingHistory],
    async () => {
      const rows = await offlineDb.shoppingItems.where('listId').equals(listId).toArray();
      const decision = decideShoppingAdd(
        rows.map(({ item }) => ({
          ...item,
          refs: item.recipeRefs,
          productId: item.product?.id ?? null,
        })),
        {
          name: input.name,
          quantity: input.quantity ?? null,
          quantityMax: input.quantityMax ?? null,
          quantityText: input.quantityText ?? null,
          unitCode: input.unitCode ?? null,
          unitText: input.unitText ?? null,
          note: input.note ?? null,
          refs: [],
        },
        { reactivateQuantity: options.reactivateQuantity, productId: input.productId ?? null },
      );
      const target = decision.targetId ? rows.find((r) => r.id === decision.targetId)?.item : null;
      const fields = {
        quantity: decision.result.quantity,
        quantityMax: decision.result.quantityMax,
        quantityText: decision.result.quantityText,
        unitCode: decision.result.unitCode,
        unitText: decision.result.unitText,
        note: decision.result.note ?? null,
      };
      let item: ShoppingListItem;
      if (target) {
        item =
          decision.outcome === 'UNCHANGED'
            ? target
            : withDisplay({
                ...target,
                ...fields,
                recipeRefs: decision.outcome === 'REACTIVATED' ? [] : target.recipeRefs,
                ...(input.groceryCategory ? { groceryCategory: input.groceryCategory } : {}),
                checked: false,
                checkedAt: null,
                checkedById: null,
                checkedByName: null,
                updatedAt: now(),
              });
      } else {
        const normalizedName = normalizeIngredientName(input.name);
        const known = await offlineDb.shoppingHistory
          .where('normalizedName')
          .equals(normalizedName)
          .first();
        item = withDisplay({
          id: crypto.randomUUID(),
          listId,
          name: input.name,
          normalizedName,
          ...fields,
          display: '',
          groceryCategory:
            input.groceryCategory ??
            known?.entry.groceryCategory ??
            classifyGroceryCategory(input.name),
          checked: false,
          note: fields.note,
          recipeRefs: [],
          position: rows.reduce((max, r) => Math.max(max, r.item.position), -1) + 1,
          ingredientId: null,
          product: null,
          checkedAt: null,
          checkedById: null,
          checkedByName: null,
          addedById: me()?.id ?? null,
          updatedAt: now(),
        });
      }
      if (decision.outcome !== 'UNCHANGED')
        await offlineDb.shoppingItems.put(toItemRow(item, true));
      await enqueue({
        kind: 'item-add',
        listId,
        entityId: item.id,
        payload: {
          // A new item keeps the id it has on this device, so whatever the user does with it
          // before the server answered (check it off, edit it) still points at the right row.
          input: {
            ...input,
            ...(target ? {} : { id: item.id }),
            reactivateQuantity: options.reactivateQuantity,
          },
          created: !target,
        } satisfies AddPayload,
      });
      await recount(listId);
      return { outcome: decision.outcome, item, previous: target ?? null };
    },
  );
  if (options.replay !== false) await replayIfOnline();
  return result;
}

/** Several items (voice chips, pasted lines): each goes through the same rules, in order. */
export async function addItemsLocal(
  listId: string,
  inputs: Array<ShoppingItemWriteInput & { reactivateQuantity?: 'NEW' | 'KEEP' }>,
): Promise<LocalAddResult[]> {
  const results: LocalAddResult[] = [];
  for (const { reactivateQuantity, ...input } of inputs)
    results.push(await addItemLocal(listId, input, { reactivateQuantity, replay: false }));
  await replayIfOnline();
  return results;
}

/** Check or uncheck. The time of the tap travels with the change: the last tap wins. */
export async function setCheckedLocal(
  item: ShoppingListItem,
  checked: boolean,
  /** The product that was scanned at the shelf becomes the item's preferred product. */
  productId?: string,
  /** What the row shows until the server answers – so the next scan matches offline too. */
  product?: NonNullable<ShoppingListItem['product']>,
): Promise<void> {
  const checkedAt = now();
  // Where it happened, when the app has a fresh position (user, 2026-09-24: a scan in the store).
  const position = checked ? lastKnownPosition() : null;
  await offlineDb.transaction('rw', [...QUEUE_TABLES(), offlineDb.shoppingHistory], async () => {
    const current = await currentItem(item);
    if (!current) return;
    item = current;
    await offlineDb.shoppingItems.put(
      toItemRow(
        {
          ...current,
          checked,
          checkedAt: checked ? checkedAt : null,
          checkedById: checked ? (me()?.id ?? null) : null,
          checkedByName: checked ? (me()?.displayName ?? null) : null,
          checkedLat: position?.lat ?? null,
          checkedLng: position?.lng ?? null,
          // A fresh check-off counts one purchase; un-checking forgets the count.
          purchasedCount: 1,
          ...(checked && productId && product ? { product } : {}),
          updatedAt: checkedAt,
        },
        true,
      ),
    );
    const payload: CheckPayload = {
      checked,
      checkedAt,
      ...(productId ? { productId } : {}),
      ...(position ? { checkedLat: position.lat, checkedLng: position.lng } : {}),
    };
    // Tapping back and forth offline leaves one queued change, not a trail of them.
    const earlier = await offlineDb.pendingShopping
      .where('entityId')
      .equals(item.id)
      .filter((a) => a.kind === 'item-check')
      .last();
    const last = await offlineDb.pendingShopping.where('entityId').equals(item.id).last();
    if (earlier && last?.id === earlier.id)
      await offlineDb.pendingShopping.update(earlier.id!, { payload });
    else await enqueue({ kind: 'item-check', listId: item.listId, entityId: item.id, payload });
    // Learned pairing (docs/partial/shopping-in-store-spec.md 6.2): known on this device at
    // once, confirmed by the server with the next sync.
    if (checked && productId) {
      await offlineDb.shoppingHistory
        .where('normalizedName')
        .equals(item.normalizedName)
        .modify((row) => {
          row.entry = { ...row.entry, lastProductId: productId };
        });
    }
    await recount(item.listId);
  });
  await replayIfOnline();
}

/**
 * The scanned product becomes the item's preferred product without checking it off (user,
 * 2026-09-24: "Scannen und zuordnen" in the item's menu). The pairing name → product is learned
 * on the device like a scan check-off; the server links the product with the next sync.
 */
export async function setItemProductLocal(
  item: ShoppingListItem,
  productId: string | undefined,
  product: NonNullable<ShoppingListItem['product']>,
): Promise<void> {
  await offlineDb.transaction('rw', [...QUEUE_TABLES(), offlineDb.shoppingHistory], async () => {
    const current = await currentItem(item);
    if (!current) return;
    await offlineDb.shoppingItems.put(toItemRow({ ...current, product, updatedAt: now() }, true));
    if (!productId) return;
    await enqueue({
      kind: 'item-update',
      listId: current.listId,
      entityId: current.id,
      payload: { productId } satisfies ShoppingItemUpdateInput,
    });
    await offlineDb.shoppingHistory
      .where('normalizedName')
      .equals(current.normalizedName)
      .modify((row) => {
        row.entry = { ...row.entry, lastProductId: productId };
      });
  });
  await replayIfOnline();
}

/**
 * What was paid, and "bought another one" – small facts about a bought item that must not
 * touch its text or quantity (spec 6.2, 6.3).
 */
export async function setItemPurchaseLocal(
  item: ShoppingListItem,
  patch: { purchasedCount?: number; price?: string | null; priceCurrency?: string },
): Promise<void> {
  await offlineDb.transaction('rw', QUEUE_TABLES(), async () => {
    const current = await currentItem(item);
    if (!current) return;
    const next: ShoppingListItem = {
      ...current,
      ...(patch.purchasedCount !== undefined ? { purchasedCount: patch.purchasedCount } : {}),
      ...(patch.price !== undefined
        ? {
            price: patch.price,
            priceCurrency: patch.price === null ? null : (patch.priceCurrency ?? null),
            priceSource: patch.price === null ? null : ('MANUAL' as const),
          }
        : {}),
      updatedAt: now(),
    };
    await offlineDb.shoppingItems.put(toItemRow(next, true));
    await enqueue({
      kind: 'item-update',
      listId: current.listId,
      entityId: current.id,
      payload: patch satisfies ShoppingItemUpdateInput,
    });
  });
  await replayIfOnline();
}

/** Text, quantity, category or note of an item – the offline path of the edit dialog. */
export async function updateItemLocal(
  item: ShoppingListItem,
  body: ShoppingItemWriteInput,
): Promise<void> {
  await offlineDb.transaction('rw', QUEUE_TABLES(), async () => {
    const current = await currentItem(item);
    if (!current) return;
    item = current;
    const next = withDisplay({
      ...item,
      name: body.name,
      normalizedName: normalizeIngredientName(body.name),
      quantity: body.quantity ?? null,
      quantityMax: body.quantityMax ?? null,
      quantityText: body.quantityText ?? null,
      unitCode: body.unitCode ?? null,
      unitText: body.unitText ?? null,
      note: body.note ?? null,
      groceryCategory: body.groceryCategory ?? item.groceryCategory,
      updatedAt: now(),
    });
    await offlineDb.shoppingItems.put(toItemRow(next, true));
    const creating = await offlineDb.pendingShopping
      .where('entityId')
      .equals(item.id)
      .filter((a) => a.kind === 'item-add' && (a.payload as AddPayload).created)
      .first();
    if (creating) {
      // Not on the server yet: fold the edit into the add.
      await offlineDb.pendingShopping.update(creating.id!, {
        payload: { input: { ...body, id: item.id }, created: true } satisfies AddPayload,
      });
    } else {
      await enqueue({
        kind: 'item-update',
        listId: item.listId,
        entityId: item.id,
        payload: { ...body, baseUpdatedAt: item.updatedAt } satisfies ShoppingItemUpdateInput,
      });
    }
  });
  await replayIfOnline();
}

export async function removeItemLocal(item: ShoppingListItem): Promise<void> {
  await offlineDb.transaction('rw', QUEUE_TABLES(), async () => {
    const current = await currentItem(item);
    if (!current) return;
    item = current;
    await offlineDb.shoppingItems.delete(item.id);
    const queued = await offlineDb.pendingShopping.where('entityId').equals(item.id).toArray();
    const neverSent = queued.some(
      (a) => a.kind === 'item-add' && (a.payload as AddPayload).created,
    );
    await offlineDb.pendingShopping.where('entityId').equals(item.id).delete();
    // An item that never reached the server simply disappears together with its add.
    if (!neverSent)
      await enqueue({ kind: 'item-remove', listId: item.listId, entityId: item.id, payload: {} });
    await recount(item.listId);
  });
  await replayIfOnline();
}

/** "Clear bought items": they leave the list, never the history. */
export async function clearBoughtLocal(listId: string): Promise<void> {
  await offlineDb.transaction('rw', QUEUE_TABLES(), async () => {
    const bought = await offlineDb.shoppingItems
      .where('listId')
      .equals(listId)
      .filter((row) => row.item.checked)
      .toArray();
    const ids = new Set(bought.map((row) => row.id));
    await offlineDb.shoppingItems.bulkDelete([...ids]);
    await offlineDb.pendingShopping
      .where('listId')
      .equals(listId)
      .filter((a) => ids.has(a.entityId))
      .delete();
    await enqueue({ kind: 'clear-bought', listId, entityId: listId, payload: {} });
    await recount(listId);
  });
  await replayIfOnline();
}

/** A new list, usable at once; the client-generated id doubles as the idempotency key. */
export async function createListLocal(body: {
  name: string;
  type?: string | null;
  householdId?: string | null;
}): Promise<ShoppingList> {
  const id = crypto.randomUUID();
  const list: ShoppingList = {
    id,
    ownerId: me()?.id ?? id,
    householdId: body.householdId ?? null,
    name: body.name,
    type: body.type ?? null,
    status: 'ACTIVE',
    itemCount: 0,
    checkedCount: 0,
    version: 0,
    createdAt: now(),
    updatedAt: now(),
  };
  await offlineDb.transaction('rw', QUEUE_TABLES(), async () => {
    await offlineDb.shoppingLists.put({ id, list, pending: true });
    await enqueue({ kind: 'list-create', listId: id, entityId: id, key: id, payload: body });
  });
  await replayIfOnline();
  return (await offlineDb.shoppingLists.get(id))?.list ?? list;
}

export async function pendingShoppingCount(): Promise<number> {
  return offlineDb.pendingShopping.count();
}
